import { v4 as uuidv4 } from 'uuid';

import { listAnimals } from '../models/animals';
import { getAssessment, saveAssessment } from '../models/assessments';
import { listSectionBsByAssessment, saveSectionB} from '../models/sectionb';
import { listSectionAsByAssessment, saveSectionA} from '../models/sectiona';
import { listSectionCsByAssessment, saveSectionC} from '../models/sectionc';
import { listBehaviorTagSets } from '../models/behaviorTags';
import { getFinalEvaluation, newFinalEvaluation, saveFinalEvaluation } from '../models/finalevaluation';
//import { listSectionCIoAsByAssessment, saveSectionCIoA, completeSectionCIoA } from '../models/sectioncioa';

import { today } from './datetime';
import { sectionAAdjectives } from './sectionAAdjectives';
import { sectionBBehaviors } from './sectionBBehaviors';

/*
{assessment_id: '', complete_a: true, complete_b: true, complete_c: true, complete_cioa: true, complete_final: true, user_id: guid, is_online: true}
*/


const sectionCbehaviors = [
  "feeding",
  "lying_down_alone",
  "lying_down_with_others",
  "stereotyping",
  "self_maintenance",
  "interaction_with_environment",
  "social_affiliative",
  "other",
  "out_of_view",
];

const sectionABehaviorKeys = sectionAAdjectives.map((cur)=>cur.key);
const sectionBBehaviorRules = sectionBBehaviors.reduce((acc, cur)=>{
  acc[cur.behaviorKey] = cur;
  return acc;
}, {});
const sectionBBehaviorKeys = Object.keys(sectionBBehaviorRules);

const descriptions = [
  "Calm and quiet.",
  "Active near the water.",
  "Seemed agitated.",
  "Following another animal.",
  "Interacting with a keeper.",
  "Standing alone.",
  "Displaying vocalizations.",
  "Investigating surroundings.",
  "Rolling in mud.",
  "Moving quickly.",
  "Lying down.",
  "Trumpeting.",
  "Slow pacing.",
  "Dust bathing.",
  "Chewing on branches.",
  "Close to the fence.",
  "Displaying social bonding.",
  "Eating from a feeder.",
  "Attempting to reach outside.",
  "Using trunk actively."
];

const speciesDescriptions = {
  "Macaca fascicularis": [
    "Scanning the troop from an elevated perch.",
    "Foraging through leaf litter and probing crevices.",
    "Brief chase and displacement between group members.",
    "Rapid locomotion across climbing structures.",
    "Manipulating enrichment item with hands and mouth.",
    "Pausing to monitor nearby movement and sounds.",
    "Engaged in social grooming with a nearby conspecific.",
    "Carrying food and retreating to a quieter corner."
  ],
  "Lemur catta": [
    "Sunbathing posture with chest oriented toward sunlight.",
    "Scent-marking substrate with deliberate tail movement.",
    "Ringed tail held high while moving across habitat.",
    "Short social grooming bout with adjacent individual.",
    "Investigating browse and selectively feeding on leaves.",
    "Alert scanning from elevated platform.",
    "Brief vocal exchange with nearby group members.",
    "Resting near conspecifics with intermittent vigilance."
  ],
  "Leptailurus serval": [
    "Stealthy pacing with focused visual attention.",
    "Paused and listened intently before sudden movement.",
    "Investigated enclosure edges with slow deliberate steps.",
    "Stalking posture observed near moving target stimulus.",
    "Brief vertical leap followed by quick repositioning.",
    "Resting in cover with periodic head lifts.",
    "Scent-marking behavior near habitat landmarks.",
    "Carrying food item to a secluded feeding spot."
  ],
  "Crocuta crocuta": [
    "Patrolling enclosure boundary with frequent scent checks.",
    "Social greeting interaction with close body contact.",
    "Chewing enrichment item with prolonged jaw activity.",
    "Brief dominance display toward nearby conspecific.",
    "Investigating novel object with repeated approaches.",
    "Pausing to listen and orient toward distant sounds.",
    "Resting in shade with intermittent vigilance.",
    "Locomoting between zones with steady purposeful gait."
  ],
  "Sus barbatus": [
    "Rooting behavior observed in soil substrate.",
    "Nose-led exploration around feeding area.",
    "Wallowing and body rubbing against enclosure surface.",
    "Foraging through scattered browse and debris.",
    "Brief social contact while moving as a pair.",
    "Pausing with ears oriented toward nearby activity.",
    "Digging and tossing substrate with snout.",
    "Resting with occasional repositioning near shelter."
  ],
  "Hydrochoerus hydrochaeris": [
    "Grazing steadily along grassy habitat edge.",
    "Partial water entry followed by calm floating.",
    "Group resting with close body proximity.",
    "Slow locomotion between water and land areas.",
    "Allogrooming behavior between nearby individuals.",
    "Alert posture with ears and head raised.",
    "Nibbling browse with repeated short feeding bouts.",
    "Scent investigation near favored resting location."
  ],
  "Buceros bicornis": [
    "Perched high and scanning enclosure from above.",
    "Bill manipulation of fruit before swallowing.",
    "Short flight between perches with wing beats audible.",
    "Preening behavior focused on wing and tail feathers.",
    "Calling vocalization followed by head and bill movement.",
    "Object tossing and catch-like bill coordination.",
    "Ground descent for brief foraging, then return to perch.",
    "Pair-associated proximity with synchronized movement."
  ]
};

const locations = [
  "North Enclosure",
  "South Enclosure",
  "East Yard",
  "West Yard",
  "Indoor Barn",
  "Training Area"
];


  const weather = [
    "Sunny",
    "Partly Cloudy",
    "Cloudy",
    "Overcast",
    "Light Rain",
    "Heavy Rain",
    "Thunderstorm",
    "Snow",
    "Fog",
    "Windy"
  ];



const getRandomSubset = (values, exclude, removeZero) => {
    // Filter out the excluded string
    const filtered = values.filter(v => v !== exclude);

    // Determine how many strings to return (between 0 and 3)
    const count = Math.floor((removeZero) ? ((Math.random() * 3) + 1) : (Math.random() * 4)); // 0,1,2,3   or  1,2,3

    // Shuffle the filtered array
    const shuffled = filtered.sort(() => Math.random() - 0.5);

    // Return the first `count` items
    return shuffled.slice(0, count);
}

const getRandomSectionAScore = () => {
    const num = Math.random() * 10;
    return Math.round(num * 10) / 10;
}

const randomCenteredValue = ({target, min, max, spread, decimals = 1}) => {
  // Average multiple uniforms to create a simple bell-shaped distribution around target.
  const bell = ((Math.random() + Math.random() + Math.random()) / 3) - 0.5;
  const value = target + (bell * spread * 2);
  const clamped = Math.max(min, Math.min(max, value));
  const factor = 10 ** decimals;
  return Math.round(clamped * factor) / factor;
};

const getProfiledSectionAScore = (score_profile = 'random') => {
  if (score_profile === 'median') {
    return randomCenteredValue({ target: 5, min: 0, max: 10, spread: 2.5, decimals: 1 });
  }
  if (score_profile === 'low') {
    return randomCenteredValue({ target: 2, min: 0, max: 10, spread: 2, decimals: 1 });
  }
  if (score_profile === 'high') {
    return randomCenteredValue({ target: 8, min: 0, max: 10, spread: 2, decimals: 1 });
  }

  return getRandomSectionAScore();
};

const getProfiledSectionBBehaviorCount = (score_profile = 'random') => {
  if (score_profile === 'median') {
    return Math.round(randomCenteredValue({ target: 6, min: 1, max: 12, spread: 2, decimals: 0 }));
  }
  if (score_profile === 'low') {
    return Math.round(randomCenteredValue({ target: 3, min: 1, max: 12, spread: 1.5, decimals: 0 }));
  }
  if (score_profile === 'high') {
    return Math.round(randomCenteredValue({ target: 10, min: 1, max: 12, spread: 1.8, decimals: 0 }));
  }

  return Math.floor(Math.random() * 10) + 3; // 3-12
};

const getRandomString = (inputs) => {
  const index = Math.floor(Math.random() * inputs.length);
  return inputs[index];
};

const getRandomTimestamp = (start, end) => {
  const diff = end.unix() - start.unix();
  const offset = Math.floor(Math.random() * diff);
  return start.clone().add(offset, 'second');
};

const randomSectionAScores = (score_profile = 'random') => {
  return sectionABehaviorKeys.reduce((acc, adjectiveKey)=>{
    acc[adjectiveKey] = getProfiledSectionAScore(score_profile);
    return acc;
  }, {});
};

const getRandomBehaviorTags = (behavior, behaviorTagsByBehaviorKey = {}) => {
  const behaviorTags = Array.isArray(behaviorTagsByBehaviorKey[behavior])
    ? behaviorTagsByBehaviorKey[behavior]
    : [];

  if (behaviorTags.length === 0) return [];

  return getRandomSubset(behaviorTags, null, true);
};

const getSectionBDescriptionsForSpecies = (species) => {
  if (!species) return descriptions;
  return speciesDescriptions[species] || descriptions;
};

const buildRandomSectionBBehaviorRecord = ({
  section_b_id,
  observed_on,
  animalList,
  behaviorTagsByBehaviorKey = {},
  sectionBDescriptions = descriptions,
}) => {
  const behavior = getRandomString(sectionBBehaviorKeys);
  const behaviorRule = sectionBBehaviorRules[behavior] || {};

  return {
    id: uuidv4(),
    section_b_id,
    observed_on,
    behavior,
    description: behaviorRule.hasDescription ? getRandomString(sectionBDescriptions) : null,
    location: behaviorRule.hasLocation ? getRandomString(locations) : null,
    associated_animals: behaviorRule.hasAssociatedAnimals ? animalList(true) : [],
    tags: getRandomBehaviorTags(behavior, behaviorTagsByBehaviorKey),
  };
};


const prepareAssessment = async({assessment_id}) => {
    try {

        const assessment = await getAssessment(assessment_id);
        console.log("fill assessment", assessment);
        if (!!assessment.id === false) throw new Error("Could not locate assessment with id: " + assessment_id);


        const animals = await listAnimals();
        const animalIds = animals.map((cur)=>(cur.id));

        const animalList = (removeZero) => {
            const otherAnimals = getRandomSubset(animalIds, assessment.animal_id, removeZero);
            console.log("fill other animals", otherAnimals);
            return otherAnimals;
        }

        const behaviorTagSets = await listBehaviorTagSets();
        const assessmentAnimal = animals.find((cur)=>cur.id === assessment.animal_id);
        const assessmentAnimalSpecies = assessmentAnimal?.species || '';
        const sectionBDescriptions = getSectionBDescriptionsForSpecies(assessmentAnimalSpecies);

        const behaviorTagsByBehaviorKey = (behaviorTagSets || [])
          .filter((cur)=>cur.species === assessmentAnimalSpecies)
          .reduce((acc, cur)=>{
            acc[cur.behavior_key] = Array.isArray(cur.tags) ? cur.tags : [];
            return acc;
          }, {});

        return {assessment, animals, animalList, behaviorTagsByBehaviorKey, sectionBDescriptions};

    } catch (error) {
        throw error;
    }
}


const fillAssessmentsWithFakeData = async ({
  assessment_id,
  complete_a,
  complete_b,
  complete_section_a = false,
  complete_section_b = false,
  complete_c,
  complete_cioa,
  complete_final,
  score_profile = 'random',
  section_a_profile = score_profile,
  section_b_profile = score_profile,
  user_id,
  is_online,
  is_overwrite = true
}) => {
	try {

		if (complete_a) {
            await fillSectionAWithFakeData({
              assessment_id,
              user_id,
              is_online,
              overwrite: is_overwrite,
              score_profile: section_a_profile,
              mark_complete: complete_section_a
            });
		}

		if (complete_b) {
            await fillSectionBWithFakeData({
              assessment_id,
              user_id,
              is_online,
              overwrite: is_overwrite,
              score_profile: section_b_profile,
              mark_complete: complete_section_b
            });
		}

		if (complete_c) {
            await fillSectionCWithFakeData({assessment_id, user_id, is_online, overwrite: is_overwrite});
		}

    if (complete_final) {
            await fillFinalEvaluationWithFakeData({assessment_id, user_id, is_online});
    }


	} catch (error) {
		throw error;
	}
}

/*
Example Payload
{
    "id": "dea7bc34-61e5-4b7a-bdcd-9a70d329479a",
    "assessment_id": "2c2fe853-2e39-4af4-bdf4-af91f6215896",
    "user_id": "9f2a577e-edb5-4a6e-b983-641bd8d70fc0",
    "observed_on": "2025-06-08T17:12:56.552Z",
    "observation_range": "08:00 - 10:15",
    "submitted_on": "2025-06-08T17:14:29.044Z",
    "completed_on": null,
    "other_animals_nearby": [
        "0f5efd11-02a0-4f3c-90ff-4089fe35fba4",
        "5f413af5-06de-424d-81a8-0e6e3e92753f"
    ],
    "content": 9.6,
    "uncomfortable": 4.4,
    "relaxed": 5.9,
    "agitated": 7.5,
    "tense": 10,
    "frustrated": 8.1,
    "wary": 9.1,
    "playful": 0,
    "sociable": 0.3,
    "lively": 0.7,
    "lethargic": 9.2,
    "sort_order": 0,
    "sync": 1749402869.044
}
*/

const fillSectionAWithFakeData = async ({
  assessment_id,
  user_id,
  is_online,
  overwrite = false,
  score_profile = 'random',
  mark_complete = false,
  now = today()
}) => {
  try {
    const {assessment, animalList} = await prepareAssessment({assessment_id})

    const allSectionAs = await listSectionAsByAssessment(assessment.id);
    console.log("fill section a", allSectionAs);

    const filteredSectionAs = allSectionAs.filter((cur)=>{
      return (overwrite === true) ? true : cur.submitted_on === null;
    });
    console.log("fill filtered section a", filteredSectionAs);

    const start = now.clone().subtract(1,'minute');

    const sectionApromiseList = filteredSectionAs.map(async (cur)=>{

      const payload = {
        ...cur,
        completed_on: mark_complete ? now : undefined,
        observed_on: start,
        submitted_on: now,
        user_id: user_id,
        other_animals_nearby: animalList(),
        ...randomSectionAScores(score_profile),
      }
      console.log("fill payload", payload);

      return await saveSectionA(payload,is_online)
    });

    console.log("fill", sectionApromiseList);
    const sectionAPromiseResults = await Promise.all(sectionApromiseList);
    console.log("fill section a promise results", sectionAPromiseResults);

    return sectionAPromiseResults;
  } catch (error) {
      throw error;
  }
}

/*
{
  "id": "56125f7e-8b43-4eed-9a41-3430542490eb",
  "assessment_id": "2c2fe853-2e39-4af4-bdf4-af91f6215896",
  "user_id": "9f2a577e-edb5-4a6e-b983-641bd8d70fc0",
  "observed_on": "2025-06-08T17:52:41.586Z",
  "observation_range": "Day 1: 08:00 - 10:15",
  "submitted_on": null,
  "completed_on": null,
  "current_weather_conditions": null,
  "other_animals_nearby": [],
  "wallowing": 0,
  "feeding": 0,
  "foraging": 0,
  "locomotion": 0,
  "stereotyping": 1,
  "environmental_interaction": 0,
  "affiliative_giving": 0,
  "affiliative_receiving": 1,
  "agonistic_giving": 1,
  "agonistic_receiving": 0,
  "playing_with_others": 0,
  "anticipating": 0,
  "behaviors": [
    {
      "id": "1eab0564-d22a-4758-9d6a-6faca8284207",
      "section_b_id": "56125f7e-8b43-4eed-9a41-3430542490eb",
      "observed_on": "2025-06-08T17:56:21.989Z",
      "behavior": "stereotyping",
      "description": "Head bobbing\nTrunk swinging",
      "location": "Pen1",
      "associated_animals": [],
      "sync": 1749405490.597
    },
    {
      "id": "b372ba65-81fc-4024-a2b2-c1d148656178",
      "section_b_id": "56125f7e-8b43-4eed-9a41-3430542490eb",
      "observed_on": "2025-06-08T17:56:30.944Z",
      "behavior": "affiliative_receiving",
      "description": "Sparring",
      "location": "",
      "associated_animals": [
        "c21d0c90-69c6-43ef-b1d1-6a71397a0475"
      ],
      "sync": 1749405490.597
    },
    {
      "id": "a9a9f869-af7a-4d3b-a581-4929dd3c4fe2",
      "section_b_id": "56125f7e-8b43-4eed-9a41-3430542490eb",
      "observed_on": "2025-06-08T17:58:10.482Z",
      "behavior": "agonistic_giving",
      "description": "fff\nChasing",
      "location": "",
      "associated_animals": [
        "8ed16928-a5f1-49bd-b500-4b692b9ae292"
      ],
      "sync": 1749405490.597
    }
  ],
  "comments": null,
  "sort_order": 0,
  "sync": 1749405490.597
}
*/

const fillSectionBWithFakeData = async ({
  assessment_id,
  user_id,
  is_online,
  overwrite = false,
  score_profile = 'random',
  mark_complete = false,
  now = today()
}) => {
  try {

    const generateBehaviorRecords = (
      section_b_id,
      start,
      end,
      animalList,
      behaviorTagsByBehaviorKey,
      sectionBDescriptions
    ) => {
      const actualCount = getProfiledSectionBBehaviorCount(score_profile);

      return Array.from({ length: actualCount }, () => {
        return buildRandomSectionBBehaviorRecord({
          section_b_id,
          observed_on: getRandomTimestamp(start, end),
          animalList,
          behaviorTagsByBehaviorKey,
          sectionBDescriptions,
        });
      });
    };


    const {assessment, animalList, behaviorTagsByBehaviorKey, sectionBDescriptions} = await prepareAssessment({assessment_id})

    const allSectionBs = await listSectionBsByAssessment(assessment.id);
    console.log("fill section b", allSectionBs);

    const filteredSectionBs = allSectionBs.filter((cur)=>{
      return (overwrite === true) ? true : cur.submitted_on === null;
    });
    console.log("fill filtered section b", filteredSectionBs);

    const start = now.clone().subtract(5,'minute');


    const promiseList = filteredSectionBs.map(async (cur)=>{

      const behaviorRecords = Array.isArray(cur.behaviors) && cur.behaviors.length > 0
        ? cur.behaviors.map((existing) => {
            const behavior = getRandomString(sectionBBehaviorKeys);
            const behaviorRule = sectionBBehaviorRules[behavior] || {};
            return {
              ...existing,
              observed_on: getRandomTimestamp(start, now),
              behavior,
              description: behaviorRule.hasDescription ? getRandomString(sectionBDescriptions) : null,
              location: behaviorRule.hasLocation ? getRandomString(locations) : null,
              associated_animals: behaviorRule.hasAssociatedAnimals ? animalList(true) : [],
              tags: getRandomBehaviorTags(behavior, behaviorTagsByBehaviorKey),
            };
          })
        : generateBehaviorRecords(
            cur.id,
            start,
            now,
            animalList,
            behaviorTagsByBehaviorKey,
            sectionBDescriptions
          );

      const payload = {
        ...cur,
        completed_on: mark_complete ? now : undefined,
        observed_on: start,
        submitted_on: now,
        user_id: user_id,
        other_animals_nearby: animalList(),
        current_weather_conditions: getRandomString(weather),
        behaviors: behaviorRecords,
      }

      console.log("fill section b payloads", payload);
      return await saveSectionB(payload,is_online);
    });


    const sectionBPromiseResults = await Promise.all(promiseList);
    console.log("fill section b promise results", sectionBPromiseResults);

    return sectionBPromiseResults;


  } catch (error) {
    throw error;
  }
}

/*
"wallowing",
"feeding",
"foraging",
"locomotion",
"stereotyping",
"environmental_interaction",
"affiliative_giving",
"affiliative_receiving",
"agonistic_giving",
"agonistic_receiving",
"playing_with_others",
"anticipating",
*/


/*

{
  "id": "857443fb-54d9-4b1e-9499-36b258cc06c3",
  "assessment_id": "2c2fe853-2e39-4af4-bdf4-af91f6215896",
  "user_id": "9f2a577e-edb5-4a6e-b983-641bd8d70fc0",
  "observation_range": "17:30",
  "observed_on": "2025-06-08T19:58:36.374Z",
  "submitted_on": null,
  "completed_on": null,
  "feeding": false,
  "lying_down_alone": false,
  "lying_down_with_others": false,
  "lying_down_with_others_animals": [],
  "stereotyping": false,
  "stereotyping_description": null,
  "self_maintenance": false,
  "self_maintenance_description": null,
  "interaction_with_environment": false,
  "interaction_with_environment_description": null,
  "social_affiliative": false,
  "social_affiliative_animals": [],
  "other": false,
  "other_description": null,
  "out_of_view": false,
  "additional_comments": null,
  "sort_order": 1,
  "sync": 1749412716.375
}

{
  "id": "d085ebf6-9216-4aed-b287-a6249f5f996b",
  "assessment_id": "2c2fe853-2e39-4af4-bdf4-af91f6215896",
  "user_id": "9f2a577e-edb5-4a6e-b983-641bd8d70fc0",
  "observation_range": "18:00",
  "observed_on": "2025-06-08T19:59:02.675Z",
  "submitted_on": "2025-06-08T20:01:44.046Z",
  "completed_on": null,

  "feeding": false,
  "lying_down_alone": false,
  "lying_down_with_others": false,
  "lying_down_with_others_animals": [],
  "stereotyping": true,
  "stereotyping_description": "stereo1",
  "self_maintenance": false,
  "self_maintenance_description": null,
  "interaction_with_environment": false,
  "interaction_with_environment_description": null,
  "social_affiliative": false,
  "social_affiliative_animals": [],
  "other": false,
  "other_description": null,
  "out_of_view": false,
  "additional_comments": "aabb",
  "sort_order": 2,
  "sync": 1749412904.047
}

*/

const fillSectionCWithFakeData = async ({assessment_id, user_id, is_online, overwrite = false, now = today()}) => {
  try {
    const {assessment, animalList} = await prepareAssessment({assessment_id})

    const allSections = await listSectionCsByAssessment(assessment.id);
    console.log("fill section c", allSections);

    const filteredSections = allSections.filter((cur)=>{
      return (overwrite === true) ? true : cur.submitted_on === null;
    });
    console.log("fill filtered section c", filteredSections);

    //const start = now.clone().subtract(1,'minute');


    const getRandomComment = () => {
      const comments = [
        "Animal was calm during the procedure.",
        "Displayed minor resistance when approached.",
        "Excellent behavior today.",
        "Required distraction techniques.",
        "Showed signs of stress near end.",
        "Very alert and responsive.",
        "Minimal interaction observed.",
        "Interacted positively with handlers.",
        "Feeding behavior normal.",
        "Vocalizations increased after noon.",
        "Grooming behavior observed.",
        "Group behavior remained stable.",
        "No unusual signs noted.",
        "Needed additional encouragement.",
        "Slightly more restless than usual.",
        "Excellent interaction with enrichment.",
        "Quick to respond to stimuli.",
        "Showed interest in surroundings.",
        "Took time to settle down.",
        "Calm and cooperative overall."
      ];

      return Math.random() < 0.5
        ? null
        : comments[Math.floor(Math.random() * comments.length)];
    };



    const promiseList = filteredSections.map(async (cur)=>{

      const localBehavior = getRandomString(sectionCbehaviors);

      const payload = {
        ...cur,
        completed_on: undefined,
        observed_on: now,
        submitted_on: now,
        user_id: user_id,
        additional_comments: getRandomComment(),

        behavior: localBehavior,
        description: (['stereotyping','self_maintenance', 'interaction_with_environment', 'other'].includes(localBehavior)) ? getRandomString(descriptions) : null,
        associated_animals: (['lying_down_with_others','social_affiliative'].includes(localBehavior)) ? animalList(true) : [],
      }
      console.log("fill payload", payload);

      return await saveSectionC(payload,is_online)
    });

    console.log("fill", promiseList);
    const promiseResults = await Promise.all(promiseList);
    console.log("fill section c promise results", promiseResults);

    return promiseResults;

  } catch (error) {
      throw error;
  }
}

const fillFinalEvaluationWithFakeData = async ({assessment_id, user_id, is_online, now = today()}) => {
  try {
    const assessment = await getAssessment(assessment_id);
    console.log("fill final evaluation assessment", assessment);
    if (!!assessment?.id === false) throw new Error("Could not locate assessment with id: " + assessment_id);

    const existingFinalEvaluation = await getFinalEvaluation(assessment.id);
    const baseRecord = existingFinalEvaluation || newFinalEvaluation();
    const submittedOn = assessment.started_on || now;

    const stereotypyOptions = [
      "head_bobbing",
      "pacing",
      "repetitive_vocalization",
      "route_tracing",
      "none_observed"
    ];
    const vocalisationOptions = [
      "none",
      "contact_calls",
      "alarm_calls",
      "low_frequency_rumble",
      "high_intensity_calls"
    ];
    const finalComments = [
      "Overall behavior remained stable throughout the assessment period.",
      "Minor variation observed, but no major welfare concerns identified.",
      "Behavioral state improved after enrichment opportunities.",
      "Animal remained engaged with habitat and social partners."
    ];
    const otherObservations = [
      "Maintained regular interaction with environmental features.",
      "Displayed intermittent vigilance with rapid return to baseline.",
      "No prolonged withdrawal from normal activity patterns.",
      "Showed expected response to nearby stimuli."
    ];
    const unexpectedSituations = [
      null,
      null,
      "Brief loud disturbance near enclosure perimeter.",
      "Temporary husbandry interruption during observation window."
    ];

    const finalPayload = {
      ...baseRecord,
      id: assessment.id,
      completed_on: submittedOn,
      stereotypies: getRandomSubset(stereotypyOptions, null, false),
      stereotypies_interfere_with_behavior: getRandomString(["never", "rarely", "sometimes", "often"]),
      can_stereotypies_be_interrupted: getRandomString(["yes", "no", "unknown"]),
      can_stereotypies_be_interrupted_description: getRandomString([
        "Interrupted by keeper presence and enrichment delivery.",
        "Interrupted briefly by social partner movement.",
        "No reliable interruption trigger observed.",
        "Interruptible with food-based redirection."
      ]),
      feeding_foraging_score: getRandomSectionAScore(),
      activity_frequency_score: getRandomSectionAScore(),
      social_animal_score: getRandomSectionAScore(),
      other_observations: getRandomString(otherObservations),
      unexpected_situations: getRandomString(unexpectedSituations),
      vocalisations: getRandomSubset(vocalisationOptions, null, false),
      mental_health: getRandomSectionAScore(),
      physical_health: getRandomSectionAScore(),
      overall_welfare: getRandomSectionAScore(),
      final_comments: getRandomString(finalComments),
    };
    console.log("fill final evaluation payload", finalPayload);

    const finalEvaluationSaveResult = await saveFinalEvaluation(finalPayload, user_id, is_online);

    const submittedAssessmentPayload = {
      ...assessment,
      submitted_on: submittedOn,
    };
    const assessmentSaveResult = await saveAssessment(
      submittedAssessmentPayload,
      assessment.facility_id,
      user_id,
      is_online
    );

    console.log("fill final evaluation save result", finalEvaluationSaveResult);
    console.log("fill assessment submission save result", assessmentSaveResult);

    return {finalEvaluationSaveResult, assessmentSaveResult};
  } catch (error) {
    throw error;
  }
}

export {fillAssessmentsWithFakeData};
